import { createAccessControl } from 'better-auth/plugins/access'
import {
  adminAc,
  defaultStatements as adminPluginStatements,
} from 'better-auth/plugins/admin/access'

/**
 * Single source of truth for FLOU's permission model. Add a resource or
 * action here once and both the server (`server.ts` → admin plugin) and the
 * browser (`client.ts` → adminClient) authorize against the same catalogue.
 *
 * NEVER reimplement this in app code. Go through `requirePermission()` in
 * `./guards.ts`, which calls `auth.api.userHasPermission`.
 *
 * FLOU is single-app / single-tenant: there is no organization plugin, so the
 * only inherited statements are the admin plugin's (`user`, `session` — the
 * resources `auth.api.banUser` / `setRole` / `impersonateUser` check against).
 *
 * ── OWNERSHIP SCOPE ────────────────────────────────────────────────────────
 * Actions grant a resource TYPE, not specific rows. Where ownership matters
 * ("my orders", "the chats of my jobs") the convention — already used by
 * `user_account` — is: the BASE action (`read`, `update`, `write`) is the
 * OWN-row scope, and `manage` widens it to EVERY row. A use-case that reads a
 * chat thread checks `chat:read` for the coarse gate, then filters to threads
 * the caller is assigned to UNLESS they also hold `chat:manage`. The row filter
 * lives in the data layer (`@repo/core`, keyed off the assignee relation), not
 * in a proliferation of `:assigned` / `:all` action strings.
 */
/**
 * The full resource → actions catalogue. Exported so the Storybook
 * `Foundations/Roles & permissions` page can render the live permission matrix
 * straight from this object (the docs must never drift from the code). Consume
 * it read-only; the source of truth is this declaration.
 */
export const statement = {
  // Inherited from the admin plugin — backs the built-in `/admin/*` endpoints.
  ...adminPluginStatements,

  // ── FLOU domain resources ──────────────────────────────────────────────
  /** Service catalogue: rentals, studios, montage packages, graphics tiers. */
  offer: ['create', 'read', 'update', 'delete', 'manage'],
  /**
   * Customer orders and their lines.
   *
   * `refund` is its own action and not a shade of `update`, because spec §2
   * gives Support exactly one power over an order and denies them the other:
   * "podgląd zamówień, zwroty w statusie Oczekujące" next to "Nie edytuje
   * zamówień". Folding it into `update` would have meant the desk that gives
   * money back could also retitle a line, and folding it into `manage` would
   * have handed them the whole order screen. It is also the CUSTOMER's own
   * power over their own line (spec §5, §8) — the base action is the own-row
   * scope, per the ownership convention above, and `manage` is what widens it
   * to anybody's line.
   *
   * `reschedule` is the third of the same kind. Spec §2 gives Support
   * "rozwiązywanie sporów" and §7 spells out what that means in the one case
   * the product already promises it — a line whose source material was wrong
   * gets a chat note reading "Support odezwie się z nowym terminem", and until
   * this action existed there was no way for them to keep it. Not `update`
   * (every performer holds it, and moving your own deadline is the one edit a
   * performer must never make), not `manage` (Support may not edit an order),
   * not `refund` (a different act with a different consequence).
   *
   * `assign` is separate for the mirror-image reason. Spec §2: "Grafik …
   * Przypisywany ręcznie przez Admina lub Koordynatora", and §16 gives the
   * coordinator "rozdzielanie zadań" as their job. The coordinator holds no
   * `order:update` (they must not retitle a line) and no `order:manage` (they
   * must not see a price — §17: "bez kwot w systemie"), so neither of the
   * existing actions could carry it. `update` would also have given it to every
   * performer, who would then be able to hand their own work to somebody else.
   */
  order: ['create', 'read', 'update', 'refund', 'assign', 'reschedule', 'delete', 'manage'],
  /** Inbound enquiries from the public forms. `create` is PUBLIC — the whole
   *  point of a contact form is that a stranger can use it; everything past
   *  that (reading the inbox, assigning, closing) is staff-side. */
  lead: ['create', 'read', 'update', 'manage'],
  /**
   * Job applications from `/pl/dolacz`.
   *
   * Its own subject rather than a shade of `lead`, because the two inboxes are
   * held by different people. Every staff preset reads the sales inbox — a
   * Support desk answering an enquiry needs to see it — and NONE of them has
   * any business reading somebody's CV, their age and where they live. Folding
   * applications into `lead:read` would have granted six presets a personnel
   * file they never asked for.
   *
   * `create` is PUBLIC for the same reason it is on `lead`: a recruitment form
   * a stranger cannot use is not a recruitment form.
   */
  recruitment: ['create', 'read', 'update', 'manage'],
  /** A user's own account + profile (billing, marketing consent, …). */
  user_account: ['create', 'read', 'update', 'delete', 'manage'],
  /** CMS content: pages, testimonials, FAQ, marketing copy. */
  content: ['create', 'read', 'update', 'delete', 'manage'],
  /** Client ↔ staff conversations, threaded per job. `manage` = every thread. */
  chat: ['read', 'write', 'manage'],
  /** Rental / studio / production calendar. `write` = book & edit; `manage` = all. */
  calendar: ['read', 'write', 'manage'],
  /** The payouts / earnings surface. ADMIN-ONLY by policy — a Manager is
   *  deliberately excluded from it (they see every other department's stats). */
  earnings: ['read', 'manage'],
  /** Per-department operational statistics (revenue, throughput). */
  stats: ['read', 'manage'],
  /** Role administration. `assign` = grant an existing preset (a Manager, with
   *  the "never touch an Admin or another Manager" guard on top); `manage` =
   *  full role administration (Admin). */
  role: ['read', 'assign', 'manage'],
  /**
   * Notices from the office to the people who work here.
   *
   * TWO actions and no middle, because there is no middle to have: you are
   * either somebody the company writes to or somebody who writes on its behalf.
   * `read` is not the ownership-scoped base action it is elsewhere — an
   * announcement has no owner, and the rows anybody sees are decided by the
   * receipts written at publication, never by this grant.
   *
   * `manage` is the office's: it sends a notice to one person or to everyone,
   * and the reason it is not `content:update` is that this reaches PEOPLE
   * rather than pages. It is the nearest thing FLOU has to a personnel power,
   * so it sits with the other one — the Menedżer's `role:assign` — and with the
   * Admin.
   */
  announcement: ['read', 'manage'],
  /**
   * The store of delivered work, for the year after the customer's week runs
   * out (umowa · punkt 15).
   *
   * ONE action, and a resource of its own rather than another `order` verb,
   * because the people who hold it are not the people any `order` action picks
   * out. The owner's rule is "magazyn widzi tylko admin i koordynator" — so
   * `order:manage` would be wrong (it misses the coordinator and lets a
   * Menedżer in) and `order:assign` would be wrong too (it would hand the
   * archive to Wynajem). A grant nobody else inherits is the only shape that
   * says exactly those two.
   *
   * It is deliberately NOT what gates a fresh hand-over: for the first seven
   * days the customer and the performer reach their own files through
   * ownership, as they always have. This is the key to the year AFTERWARDS.
   */
  archive: ['read'],
  /**
   * The training rehearsal desk (`/pl/panel/test`).
   *
   * ONE action, held by ONE role, and it has to be its own resource rather than
   * a shade of `order:manage`: the desk invents accounts, moves an order
   * through every stage without anybody paying for anything, and deletes the
   * lot. A Menedżer holds `order:manage` and must not hold any of that.
   *
   * Owner's decision: „stwórz osobną zakładkę »Test« dostępną wyłącznie dla
   * administratora". The tab, every action behind it and the teardown all ask
   * for this one grant, so „wyłącznie dla administratora" is a fact about the
   * permission catalogue rather than about which links happen to be drawn.
   */
  training: ['manage'],
} as const

export const ac = createAccessControl(statement)

export type Statement = typeof statement
export type Resource = keyof Statement
export type Action<R extends Resource> = Statement[R][number]

// ────────────────────────────────────────────────────────────────────────
// Roles. Three tiers:
//
//   • BASE roles — the surface boundary stored natively on `user.role`:
//     `client` (storefront), `employee` (the staff baseline), `admin` (all).
//   • DESK presets — named `employee`-tier bundles that actually DIFFER from
//     the baseline: Support, Studio, Wynajem, Menedżer. Each is a STATIC
//     `ac.newRole` built on the employee grants plus its own deltas, and each
//     delta is a real one — `chat:manage`, `calendar:write`, `order:manage`.
//     A preset with no delta is not a preset, it is a synonym, which is why
//     the four craft roles that used to sit here are gone.
//   • LEVELS (`user_skill`) — what a `employee` may TAKE, per product and per
//     class, with a per-person rate. This is where Grafik / Montażysta /
//     Fotograf / Operator live now, and where CLEAN / DYNAMIC / ADVANCED always
//     lived. Never in this catalogue: it is data an Admin retunes, while roles
//     are a fixed set the code branches on.
//   • MARKERS — a token worn ON TOP of a main role that names a duty rather
//     than a reach: `meetings` (Spotkania). It grants nothing past the
//     own-account floor, is never the role an account is GIVEN, and opens no
//     surface on its own. See `meetings` below and {@link MARKER_ROLES}.
//
// Ownership scoping is ROW-level, not role-level (see the header): a preset
// holding `order:['read','update']` may touch the rows it is ASSIGNED, and the
// data layer enforces that; `manage` is what widens the view to every row.
// ────────────────────────────────────────────────────────────────────────

/** Registered end customer — the default role every sign-up lands as. */
export const client = ac.newRole({
  // Their own orders (create at checkout, read history, cancel a line nobody
  // has started). Row-scoped by caller — `cancelItem` checks that the order is
  // theirs, so `refund` here can only ever reach their own line (spec §5, §8).
  order: ['create', 'read', 'refund'],
  // A signed-in customer using a contact form is still just filing an enquiry.
  lead: ['create'],
  // And a customer who wants to work here is still just filing an application.
  // Being signed in must not be what decides whether the form works.
  recruitment: ['create'],
  // Their own profile. Row-scoped by caller.
  user_account: ['read', 'update'],
  // The chat threads on their own jobs.
  chat: ['read', 'write'],
})

/**
 * The staff baseline — the least a FLOU employee holds, and the base every
 * department preset extends. Read the catalogue, work the orders assigned to
 * them, talk to those clients, read the shared calendar. NOTE this is a leaner
 * grant than the old `editor` role it replaces: catalogue/content editing
 * (`offer:manage` / `content:manage`) is now an Admin capability, not something
 * every staff member holds.
 */
const employeeGrants = {
  offer: ['read'],
  order: ['read', 'update'],
  lead: ['read', 'update'],
  content: ['read'],
  chat: ['read', 'write'],
  calendar: ['read'],
  /**
   * Their OWN account — the same grant the customer holds, and for the same
   * reason: under the ownership convention the base action is the own row, so
   * this reaches exactly one account, the caller's. Widening to somebody else's
   * still needs `user_account:manage`, which is Admin's alone.
   *
   * Missing until the e2e sweep found it, and the omission was not cosmetic:
   * `loadMyAccount` — which every staff member hits on `/pl/panel` itself and
   * on all four Preferencje screens — asks `getUser` for the session's own row
   * and THROWS when the answer is `forbidden`. So the panel's front page and a
   * person's own profile crashed into `error.tsx` for seven of the eleven
   * roles. It read as an infrastructure fault, which is exactly what the throw
   * was written to mean; the cause was that a Grafik had no right to know their
   * own name.
   */
  user_account: ['read', 'update'],
  /** Notices the office sends them. Everybody who works here is written to. */
  announcement: ['read'],
} as const

export const employee = ac.newRole({ ...employeeGrants })

// ── Department presets (employee-tier) ──────────────────────────────────

/*
 * ── THERE IS NO Grafik / Montażysta / Fotograf / Operator PRESET ─────────
 *
 * There were four, and all four were `ac.newRole({ ...employeeGrants })` —
 * character for character the baseline, with no delta between them. Four names
 * for one set of permissions, which meant four things to keep in step, four
 * rows in every matrix, four branches nobody could tell apart, and a question
 * ("may a Fotograf do this?") whose answer never depended on the word Fotograf.
 *
 * What actually separates these people is not what they may REACH, it is what
 * they may TAKE — and that already has a home: `user_skill`, one grant per
 * person per level, with its own rate. A montażysta who does Pro reels and
 * Starter films is two rows there, which is a distinction no role could hold
 * anyway: a role is one token on an account, and the levels are per product.
 *
 * So the fach is a GRANT and the permission is `employee`. Owner's decision:
 * "nie ma sensu robić ról osobnych, wystarczy pracownik i do tego poziomy."
 *
 * The desks below did NOT collapse with them, and the reason is visible in
 * their bodies rather than in their names: `support` holds `chat:manage`,
 * `rental` and `studio` hold `calendar:write`, `manager` holds `order:manage`
 * and `stats`. Those are real differences in reach, and no level replaces them
 * — there is no such thing as a Support Pro.
 */

/**
 * Support — chats and a read-only calendar; no order edits.
 *
 * `chat:manage` and not just `read`, because the whole job description in spec
 * §2 begins "Wszystkie czaty" and §16's Support panel is a list of every
 * conversation. Under the ownership convention above, `chat:read` alone would
 * mean "the threads you are personally in" — which for Support is none of them,
 * since Support is never the customer and never the assigned performer. The
 * distinction `manage` draws here is scope, not power: it opens every thread and
 * grants no order edits, no refunds outside `Oczekujące`, and no finances.
 */
export const support = ac.newRole({
  offer: ['read'],
  // Spec §2, and the two halves are deliberately one grant apart: "zwroty w
  // statusie Oczekujące" YES, "Nie edytuje zamówień" — so `refund` and not
  // `update`. `cancelItem` refuses anything past `Oczekujące` for everyone
  // alike, so this cannot reach a line a performer has already started.
  order: ['read', 'refund', 'reschedule'],
  lead: ['read', 'update'],
  chat: ['read', 'write', 'manage'],
  calendar: ['read'],
  // Support does not extend `employeeGrants` (the order actions differ), so the
  // own-account grant has to be repeated here rather than inherited — and the
  // announcements grant with it, for the same reason.
  user_account: ['read', 'update'],
  announcement: ['read'],
})

/** Studio — runs the room calendar (rental slots + our own production shoots). */
export const studio = ac.newRole({
  ...employeeGrants,
  // Spec §16's Koordynator: "rozdzielanie zadań" — placing other people's work
  // — with `read` and NOT `update` beside it, because the same section keeps
  // them away from the amounts and the edit controls.
  order: ['read', 'assign'],
  calendar: ['read', 'write'],
  // Umowa · punkt 15: the archive is the Admin's and the coordinator's. It is
  // the one thing on this preset that outlives a job — everything else here is
  // about work in front of them.
  archive: ['read'],
})

/**
 * Wynajem sprzętu — the gear calendar, and the clients of those hires.
 *
 * The ONE department preset that does not extend `employeeGrants`, and the two
 * omissions are the point rather than an oversight (owner's decision on the
 * panel review): this desk runs a shelf and a calendar, so it holds neither the
 * catalogue nor the inbox.
 *
 *   • No `offer`. The price list is somebody else's screen, and `/pl/panel/oferta`
 *     now refuses this role outright. The equipment POOL is unaffected — both
 *     `listPools` ("Sprzęt") and `listBookable` ("Nowa rezerwacja") gate on
 *     `calendar:read`, which this preset holds, so the two catalogue screens
 *     this desk actually works in keep opening.
 *   • No `lead`. The contact-form inbox belongs to the Admin and the
 *     coordinator who triages it; a gear desk is neither.
 *
 * `order:['read','update']` STAYS, and is not a leftover: a hire's own line
 * moves through these hands (approval, refusal — `orders/rental-approval.ts`),
 * reached from the booking it belongs to rather than from an orders list.
 * `assign` likewise covers spec §16's "rozdzielanie zadań".
 */
export const rental = ac.newRole({
  order: ['read', 'update', 'assign'],
  content: ['read'],
  chat: ['read', 'write'],
  // Their own row, exactly as everywhere else — see `employeeGrants`.
  user_account: ['read', 'update'],
  announcement: ['read'],
  calendar: ['read', 'write'],
})

/**
 * Menedżer — every department's statistics EXCEPT earnings, all chats, a
 * writable calendar, and role administration (assign existing presets, with the
 * "never an Admin or another Manager" guard enforced above the admin plugin in
 * the user-management action). Deliberately holds NO `earnings` grant.
 */
export const manager = ac.newRole({
  offer: ['read'],
  order: ['read', 'update', 'refund', 'assign', 'reschedule', 'manage'],
  lead: ['read', 'update', 'manage'],
  content: ['read', 'update'],
  // `update` beside `read` for the same own-row reason as everyone else: a
  // Menedżer could open their profile and not save it. Neither action reaches
  // another account without `manage`, which stays Admin's.
  user_account: ['read', 'update'],
  chat: ['read', 'write', 'manage'],
  calendar: ['read', 'write', 'manage'],
  stats: ['read', 'manage'],
  role: ['read', 'assign'],
  // The personnel desk. Whoever may hand somebody a role is who may tell them
  // about it — and the fourteen-day notice the framework contract requires
  // before terms change is written from this seat.
  announcement: ['read', 'manage'],
  // The same seat, one step earlier: whoever hands out roles is who reads the
  // applications for them. It stops here — no other staff preset holds it.
  recruitment: ['read', 'update', 'manage'],
})

/** Full operator — everything, including user administration and finance. */
export const admin = ac.newRole({
  offer: ['create', 'read', 'update', 'delete', 'manage'],
  order: ['create', 'read', 'update', 'refund', 'assign', 'reschedule', 'delete', 'manage'],
  lead: ['create', 'read', 'update', 'manage'],
  recruitment: ['create', 'read', 'update', 'manage'],
  user_account: ['create', 'read', 'update', 'delete', 'manage'],
  content: ['create', 'read', 'update', 'delete', 'manage'],
  chat: ['read', 'write', 'manage'],
  calendar: ['read', 'write', 'manage'],
  earnings: ['read', 'manage'],
  stats: ['read', 'manage'],
  role: ['read', 'assign', 'manage'],
  announcement: ['read', 'manage'],
  archive: ['read'],
  training: ['manage'],
  // The admin-plugin resources (`user:['ban','impersonate',…]`,
  // `session:['revoke',…]`). Without these grants `auth.api.banUser` /
  // `setRole` / `impersonateUser` return FORBIDDEN even for this role.
  ...adminAc.statements,
})

/**
 * Spotkania — the Admin who takes the meetings. A MARKER, not a desk.
 *
 * Owner: „nie każdy admin to robi, dlatego potrzebna jest nowa rola
 * »SPOTKANIA«. Admin który ma tę rolę przyjmuje zamówienia ze Strategii,
 * Zapytań przez formularz etc." So the token answers WHO, not WHAT: whose
 * calendar decides whether a strategy hour or a free call can still be
 * offered, and who is put on the meeting once it is taken (`listMeetingHosts`
 * and `booking_host` in `@repo/db`).
 *
 * ── WHY A ROLE AND NOT A LEVEL ─────────────────────────────────────────────
 * A level is keyed on a variant and pulls the person into rates and payouts.
 * The free call has no variant at all, and nobody is paid per meeting. A role
 * token is what the code already routes work by.
 *
 * ── WHY IT GRANTS (ALMOST) NOTHING ─────────────────────────────────────────
 * It is only ever worn beside `admin` — `setMeetingHost` refuses any other
 * account and {@link isMeetingHost} asks for both tokens — and an Admin
 * already holds every statement. Any grant here would therefore be dead on
 * the one account it is meant for, and a door on a hand-edited one. The single
 * grant is the own-account floor every token in this catalogue carries
 * (`own-account.test.ts`), which reaches nothing an Admin lacks. That is also
 * why the "a preset with no delta is a synonym" rule above does not bite: this
 * is not a cheaper Admin, it is a mark on one.
 *
 * Never a MAIN role: it is absent from {@link MAIN_ROLES}, so the role cards,
 * the assistant's `assign_role` and the preview picker never offer it, and
 * `assignRole` refuses it outright. It is absent from {@link STAFF_ROLES} too,
 * because a marker on its own must not be what opens the panel.
 */
export const meetings = ac.newRole({
  user_account: ['read', 'update'],
})

/**
 * Role registry — the object shape both the admin plugin (`server.ts`) and the
 * adminClient (`client.ts`) take as `roles`. Keys are the strings stored in the
 * `user.role` column (comma-separated when a user holds more than one).
 */
export const roles = {
  client,
  employee,
  support,
  studio,
  rental,
  manager,
  admin,
  meetings,
} as const

export type AppRole = keyof typeof roles

/**
 * Every role name: the main roles least- to most-privileged, then the markers
 * that are worn on top of one ({@link MARKER_ROLES}).
 */
export const ALL_ROLES: AppRole[] = [
  'client',
  'employee',
  'support',
  'studio',
  'rental',
  'manager',
  'admin',
  'meetings',
]

/** The meetings marker, „Spotkania". See the `meetings` preset above. */
export const MEETING_HOST_ROLE = 'meetings' satisfies AppRole

/**
 * The main role a meetings host must ALSO hold. Owner: „admin który ma tę
 * rolę", so the checkbox is offered on Admin accounts and nowhere else.
 */
export const MEETING_HOST_BASE_ROLE = 'admin' satisfies AppRole

/**
 * Tokens worn ON TOP of a main role and never one themselves.
 *
 * A list rather than a single constant because the question every caller asks
 * is "may this token be somebody's role?", and that question does not change
 * when a second marker arrives. Roles are stored comma-separated, so a marker
 * costs no schema; what it costs is that every single-choice surface has to
 * leave it out, which is what {@link MAIN_ROLES} is for.
 */
export const MARKER_ROLES: readonly AppRole[] = [MEETING_HOST_ROLE]

/** Is this token a marker — something an account WEARS, never something it IS? */
export const isMarkerRole = (role: string): boolean =>
  (MARKER_ROLES as readonly string[]).includes(role)

/**
 * The roles an account can BE: every role but the markers.
 *
 * What the single-choice role cards, the assistant's `assign_role` and
 * `assignRole` itself accept. A marker offered there would replace the main
 * role — one confirmed tap on „Spotkania" would strip an Admin of `admin` and
 * log them out.
 */
export const MAIN_ROLES: AppRole[] = ALL_ROLES.filter((role) => !isMarkerRole(role))

/**
 * Roles that identify actual FLOU staff — every MAIN role except the default
 * `client`. Used by the `/panel` gate: any of these may reach the operations
 * panel.
 *
 * The markers are out on purpose. `meetings` only means something beside
 * `admin`, which is staff already, and on its own it must not open anything.
 */
export const STAFF_ROLES: AppRole[] = MAIN_ROLES.filter((role) => role !== 'client')

/**
 * The role tokens on an account, parsed. The admin plugin stores them as ONE
 * comma-separated string (`"employee,manager"`), so every caller that wants a
 * list has to split it — and four places were doing it by hand, each with its
 * own idea of whitespace and unknown values.
 */
export function rolesOf(raw: string | null | undefined): AppRole[] {
  return (raw ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter((part): part is AppRole => (ALL_ROLES as string[]).includes(part))
}

/**
 * Does ONE role preset grant `resource:action`?
 *
 * The single local reading of the catalogue, and every other "what may this
 * role do" in the codebase now goes through it: the panel's `canForRole` ORs it
 * across the tokens of a multi-role account, and `requirePermission` asks it
 * about the preview overlay's role before it asks better-auth about the real
 * one. Two implementations of this arithmetic would be two opinions about who
 * may see Finanse.
 *
 * NOT a guard, and cheaper than one: `auth.api.userHasPermission` answers the
 * same question over the wire and is what the guards call. This reads the same
 * declarations above, so the two cannot disagree without the catalogue itself
 * changing.
 *
 * `resource` and `action` are plain strings rather than the typed pair, because
 * the callers hand them on from their own untyped boundaries (a nav definition,
 * a cookie's role). An unknown resource simply grants nothing.
 */
export function roleGrants(role: AppRole, resource: string, action: string): boolean {
  // `Role.statements` is better-auth's typed bundle keyed by the literal
  // resource names of `statement`; indexing it by a runtime string needs the
  // wider shape, and the invariant is that every value is a list of actions.
  const grants = roles[role].statements as unknown as Record<string, readonly string[] | undefined>
  return grants[resource]?.includes(action) ?? false
}

/**
 * Does this account hold ANY staff role?
 *
 * The panel is one surface for everyone signed in; what differs is which
 * sections exist. This answers that question — it is a presentation decision,
 * not the guard: each staff-only route still asserts its own capability, and a
 * client's own orders are row-scoped to their session id regardless.
 */
export function isStaffRole(raw: string | null | undefined): boolean {
  const staff: string[] = STAFF_ROLES
  return rolesOf(raw).some((role) => staff.includes(role))
}

/**
 * Roles allowed to reach the built-in admin-plugin endpoints (ban, impersonate,
 * set-role, list users). Only the full operator today — the Manager's scoped
 * role-assignment lands with the user-management surface, wired with its
 * "never an Admin or another Manager" guard rather than a blanket grant here.
 */
export const ADMIN_ROLES: AppRole[] = ['admin']

/**
 * Who may turn the role-preview overlay on (`/pl/panel/podglad`).
 *
 * The Admin alone, and deliberately NOT the Menedżer even though they
 * administer roles: assigning somebody a preset is a decision about another
 * account, while wearing a preset is a change to what THIS session may reach.
 * The overlay can only ever narrow (see `preview-role.ts`), so widening this
 * list would not hand anybody a capability — but it would hand them a panel
 * that no longer matches the one they are accountable for.
 */
export const PREVIEW_OPERATOR_ROLES: AppRole[] = ['admin']

/** May this account look at the panel through somebody else's permissions? */
export function isPreviewOperator(raw: string | null | undefined): boolean {
  const operators: string[] = PREVIEW_OPERATOR_ROLES
  return rolesOf(raw).some((role) => operators.includes(role))
}

/**
 * The roles that can be PREVIEWED — every main role but the operator's own.
 *
 * `admin` is absent because "preview as admin" is not a state: it is the
 * absence of the overlay, and offering it as a choice would give the mode two
 * ways to be off, only one of which clears the cookie and the indicator.
 *
 * The markers are absent because a preview shows ONE token, and a marker on
 * its own is a panel nobody has: "Spotkania" without the Admin under it.
 */
export const PREVIEWABLE_ROLES: AppRole[] = MAIN_ROLES.filter(
  (role) => !(PREVIEW_OPERATOR_ROLES as string[]).includes(role),
)

/**
 * Does this account take the company's meetings?
 *
 * BOTH tokens, not just the marker. The role is offered on Admin accounts only,
 * and a marker that survived on a demoted account (an older write, a row edited
 * by hand) must not keep sending a former Admin the customers' meetings. The
 * SQL twin is `listMeetingHosts` in `@repo/db`, which asks the same two
 * questions and also leaves out banned (closed) accounts.
 */
export function isMeetingHost(raw: string | null | undefined): boolean {
  const held = rolesOf(raw)
  return held.includes(MEETING_HOST_ROLE) && held.includes(MEETING_HOST_BASE_ROLE)
}

/** May this account carry the meetings marker at all? What the checkbox is drawn for. */
export function mayHostMeetings(raw: string | null | undefined): boolean {
  return rolesOf(raw).includes(MEETING_HOST_BASE_ROLE)
}

/**
 * Roles that DO THE WORK and are therefore paid per job (spec §16, §17 · J2).
 *
 * The set behind `/pl/panel/zarobki`. It is a list of roles rather than a
 * capability check, and that is not a shortcut — it is the honest shape. The
 * `earnings` grant means "everybody's payouts" and is Admin-only by policy;
 * a Freelancer's own earnings are not a weaker version of it, they are a
 * different question about their own rows, and `myEarnings` in `@repo/core`
 * answers it without taking a user id at all.
 *
 * `support` and `manager` are absent deliberately. Support is not assigned
 * lines, and spec §17 is explicit about the coordinator: "umowa o pracę, bez
 * kwot w systemie." `studio` and `rental` are absent for the same reason —
 * they mind a room and a shelf, and neither is paid per finished line.
 */
export const PERFORMER_ROLES: AppRole[] = ['employee']

/**
 * Roles that read EVERY order, not just their own row (spec §2's role table).
 *
 * Support ("podgląd zamówień"), the coordinator (who splits the work up and
 * therefore has to see it), the Menedżer and the Admin. A performer is
 * deliberately absent: spec §16 gives them "przydzielone zlecenia" and nothing
 * wider, and their Zamówienia tab is their own PURCHASES — a freelancer is also
 * a customer, and the two are not the same list.
 *
 * A role set rather than a capability, for the same reason {@link PERFORMER_ROLES}
 * is one: the catalogue has no action that separates the desk from the bench.
 * `order:read` is held by a customer for their own rows, `order:update` by the
 * people who WORK the lines, and `order:manage` is power rather than scope —
 * granting it to Support would hand them the editing controls spec §2 denies
 * them in the same sentence ("Nie edytuje zamówień").
 *
 * Applied in TWO places, and both are needed. The orders screen uses it to pick
 * which of the two lists to render; `CoreContext.seesEveryOrder` carries the
 * same answer into `core.orders.listOrders`, so the rule survives a caller that
 * never touches the screen — which is exactly what the panel assistant's
 * `list_orders` tool is. It used to hold only in the screen, and the assistant
 * went straight past it.
 */
export const ORDER_DESK_ROLES: AppRole[] = ['support', 'studio', 'rental', 'manager', 'admin']

/** Does this account see the whole order book, or only its own purchases? */
export function isOrderDeskRole(raw: string | null | undefined): boolean {
  const desk: string[] = ORDER_DESK_ROLES
  return rolesOf(raw).some((role) => desk.includes(role))
}

/**
 * Staff whose whole job IS the calendar (spec §16's Koordynator).
 *
 * They get a Kalendarz tab of their own — the full booking calendar rather than
 * the digest — and a work page cut down to what a calendar desk does, because
 * the eight-block page was showing this person the company's orders, quotes,
 * work queue, deadline watch and inbox, none of which they may act on.
 *
 * A role set rather than a capability, for the same reason {@link PERFORMER_ROLES}
 * is one: `calendar:write` is also held by the Menedżer and the Admin, whose
 * panel is the whole company and who reach the same calendar from the
 * Rezerwacje block. The catalogue has no action meaning "and nothing else".
 *
 * `rental` alone today. `studio` is the same shape on a different resource —
 * the room instead of the shelf — and is the obvious next member, but it also
 * splits work up (`order:assign`, with a coordination screen behind it), so it
 * keeps the full work page until somebody decides otherwise.
 */
export const CALENDAR_DESK_ROLES: AppRole[] = ['rental']

/**
 * Is this account a calendar desk and NOTHING else?
 *
 * "Nothing else" is load-bearing. Roles are stored comma-separated, so an
 * `admin,rental` account would otherwise lose the Admin's panel because one of
 * its tokens is a desk. Every token has to be in the set before the cut-down
 * panel applies — a wider account keeps the wider screen.
 */
export function isCalendarDeskRole(raw: string | null | undefined): boolean {
  const held = rolesOf(raw)
  const desk: string[] = CALENDAR_DESK_ROLES
  return held.length > 0 && held.every((role) => desk.includes(role))
}

/** Is this account one that finishes jobs for money? */
export function isPerformerRole(raw: string | null | undefined): boolean {
  const performers: string[] = PERFORMER_ROLES
  return rolesOf(raw).some((role) => performers.includes(role))
}

/**
 * Roles that administer other roles — they hold `role:assign`/`role:manage`. A
 * non-admin (Menedżer) may never grant one of these nor act on a user who holds
 * one: enforced in `@repo/core`'s `assignRole` and MIRRORED by the panel's UI
 * affordances. Exported as the ONE source of truth so the guard and the UI can
 * never drift.
 */
const PRIVILEGED_ROLES: ReadonlySet<string> = new Set(['admin', 'manager'] satisfies AppRole[])
export const isPrivilegedRole = (role: string): boolean => PRIVILEGED_ROLES.has(role)

/**
 * One sentence per role, in the words of someone assigning it.
 *
 * Beside {@link ROLE_LABELS} rather than in the panel, for the same reason the
 * labels are here: a role picker that describes "Menedżer" differently from the
 * grants declared thirty lines up is worse than one that says nothing. These
 * are a reading of the presets ABOVE — change a preset, change its sentence.
 *
 * Deliberately about what the person DOES, not which statements they hold: the
 * permission matrix is on the Storybook Foundations page for anyone who needs
 * it, and "lead:['read','update','manage']" answers no question an admin asks
 * while looking at a list of people.
 */
export const ROLE_DESCRIPTIONS: Record<AppRole, string> = {
  client: 'Klient. Widzi wyłącznie swoje zamówienia, rezerwacje i rozmowy o nich.',
  employee:
    'Pracownik. Przypisane zlecenia, rozmowy z ich klientami i kalendarz do wglądu. Co realizuje i w jakiej klasie, decydują nadane poziomy.',
  support: 'Obsługa klienta — wszystkie rozmowy i zgłoszenia. Nie edytuje zamówień.',
  studio: 'Studio. Prowadzi kalendarz sali — wynajmy i zdjęcia własne.',
  rental: 'Wynajem sprzętu. Kalendarz sprzętu i klienci tych wypożyczeń.',
  manager:
    'Menedżer. Wszystkie zamówienia, leady, kalendarz i statystyki, plus nadawanie ról działowych. Bez dostępu do zarobków.',
  admin: 'Admin. Pełny dostęp — katalog, użytkownicy, finanse, blokowanie kont.',
  meetings:
    'Spotkania. Dodatek do konta Admina. Ta osoba prowadzi spotkania ze strategii oraz z formularza kontaktowego.',
}

/** Human labels (pl) for each role — the one place the UI reads them from. */
export const ROLE_LABELS: Record<AppRole, string> = {
  client: 'Klient',
  employee: 'Pracownik',
  support: 'Support',
  studio: 'Studio',
  rental: 'Wynajem sprzętu',
  manager: 'Menedżer',
  admin: 'Admin',
  meetings: 'Spotkania',
}
